import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/lib/supabase'
import {
  getUnitWorkshopsForCohorts,
  getUnitsWithProgress,
  getStudentWorkshopAttendance
} from '@/lib/db'
import type {
  UnitWorkshop,
  UnitWithProgress,
  WorkshopAttendance
} from '@/types'
import {
  ArrowLeft,
  Video,
  Clock,
  Play,
  Lock,
  Radio,
  CheckCircle,
  Award,
  Users,
  Loader2,
  Calendar,
  ChevronRight,
  AlertTriangle,
  X
} from 'lucide-react'

function openMeetingUrl(rawUrl: string | null | undefined) {
  const trimmed = rawUrl?.trim() || ''
  if (!trimmed) {
    alert('No hay un enlace configurado para este taller.')
    return
  }
  const normalized = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed.replace(/^\/+/, '')}`
  const win = window.open(normalized, '_blank', 'noopener,noreferrer')
  if (!win) {
    alert(`Tu navegador bloqueó la ventana emergente. Abre el taller manualmente:\n\n${normalized}`)
  }
}

export default function Workshops() {
  const { student, isInCohort, cohortIds } = useAuth()
  const isPremium = student?.subscription_status === 'active'
  const hasFullAccess = isPremium && isInCohort
  const isPaidWaiting = isPremium && !isInCohort

  const [isLoading, setIsLoading] = useState(true)
  const [workshops, setWorkshops] = useState<UnitWorkshop[]>([])
  const [units, setUnits] = useState<UnitWithProgress[]>([])
  const [attendanceMap, setAttendanceMap] = useState<Record<string, WorkshopAttendance>>({})
  const [selectedWorkshop, setSelectedWorkshop] = useState<UnitWorkshop | null>(null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showNotAvailableModal, setShowNotAvailableModal] = useState(false)
  const [isRegistering, setIsRegistering] = useState(false)

  useEffect(() => {
    if (student) loadData()
  }, [student])

  const loadData = async () => {
    if (!student) return
    setIsLoading(true)
    try {
      const [workshopsRes, unitsRes, attendanceRes] = await Promise.all([
        cohortIds.length > 0 ? getUnitWorkshopsForCohorts(cohortIds) : Promise.resolve({ data: [], error: null }),
        getUnitsWithProgress(student.id),
        getStudentWorkshopAttendance(student.id)
      ])

      if (workshopsRes.error) console.error('Workshops: error', workshopsRes.error)
      if (unitsRes.error) console.error('Workshops: units error', unitsRes.error)
      if (attendanceRes.error) console.error('Workshops: attendance error', attendanceRes.error)

      const workshopsData = workshopsRes.data || []
      console.log('Workshops: loaded', workshopsData.length)
      setWorkshops(workshopsData)
      if (unitsRes.data) setUnits(unitsRes.data)

      if (attendanceRes.data) {
        const map: Record<string, WorkshopAttendance> = {}
        for (const a of attendanceRes.data) {
          map[a.workshop_id] = a
        }
        setAttendanceMap(map)
      }
    } catch (err) {
      console.error('Error loading workshops:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const getTitle = (w: UnitWorkshop) => {
    return w.title_es || w.title || ''
  }

  const getDescription = (w: UnitWorkshop) => {
    return w.description_es || w.description || ''
  }

  const getUnitStatus = (unitId: string) => {
    const unit = units.find(u => u.id === unitId)
    return unit?.progress?.status || 'locked'
  }

  const isWorkshopAvailable = (workshop: UnitWorkshop) => {
    const now = new Date()
    const workshopDate = new Date(workshop.scheduled_date)
    const tenMinutesBefore = new Date(workshopDate.getTime() - 10 * 60 * 1000)
    const oneHourAfter = new Date(workshopDate.getTime() + 60 * 60 * 1000)
    return now >= tenMinutesBefore && now <= oneHourAfter
  }

  const formatWorkshopDateTime = (workshop: UnitWorkshop) => {
    const date = new Date(workshop.scheduled_date)
    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleWorkshopClick = (workshop: UnitWorkshop) => {
    const alreadyAttended = attendanceMap[workshop.id]?.status === 'attended'

    // Si ya asistió, abrir el link directamente sin modal
    if (alreadyAttended) {
      openMeetingUrl(workshop.meeting_url)
      return
    }

    setSelectedWorkshop(workshop)
    if (isWorkshopAvailable(workshop)) {
      setShowConfirmModal(true)
    } else {
      setShowNotAvailableModal(true)
    }
  }

  const handleConfirmAttendance = async () => {
    if (!selectedWorkshop || !student) return
    setIsRegistering(true)
    try {
      const { data: existing, error: selectError } = await (supabase
        .from('workshop_attendance') as any)
        .select('id')
        .eq('workshop_id', selectedWorkshop.id)
        .eq('student_id', student.id)
        .maybeSingle()

      if (selectError) {
        console.error('Error consultando asistencia:', selectError)
      }

      let saveError: any = null
      if (existing) {
        const { error } = await (supabase.from('workshop_attendance') as any)
          .update({ status: 'attended', attended_at: new Date().toISOString() })
          .eq('id', existing.id)
        saveError = error
      } else {
        const { error } = await (supabase.from('workshop_attendance') as any)
          .insert({
            workshop_id: selectedWorkshop.id,
            student_id: student.id,
            cohort_id: cohortIds?.[0] || null,
            status: 'attended',
            attended_at: new Date().toISOString()
          })
        saveError = error
      }

      if (saveError) {
        console.error('Error guardando asistencia:', saveError)
        alert(`Error al registrar asistencia: ${saveError.message || saveError.code}`)
        setIsRegistering(false)
        return
      }

      // Actualizar el mapa local
      setAttendanceMap(prev => ({
        ...prev,
        [selectedWorkshop.id]: {
          ...prev[selectedWorkshop.id],
          workshop_id: selectedWorkshop.id,
          status: 'attended'
        } as WorkshopAttendance
      }))

      setShowConfirmModal(false)

      openMeetingUrl(selectedWorkshop.meeting_url)
    } catch (error: any) {
      console.error('Error:', error)
      alert(`Error: ${error?.message || 'Error desconocido'}`)
    } finally {
      setIsRegistering(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver al dashboard
        </Link>
        <h1 className="text-2xl font-bold">
          Talleres en vivo
        </h1>
        <p className="text-muted-foreground mt-1">
          Un taller por unidad — participa en vivo o mira la grabación
        </p>
      </div>

      {/* Premium lock or waiting */}
      {!hasFullAccess && (
        <div className="bg-card rounded-xl border overflow-hidden">
          <div className="p-12 text-center">
            {isPaidWaiting ? (
              <>
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Clock className="w-10 h-10 text-blue-500" />
                </div>
                <h2 className="text-xl font-bold mb-3">
                  Estamos preparando tu plataforma
                </h2>
                <p className="text-muted-foreground max-w-md mx-auto">
                  En breve tendrás acceso completo a los talleres en vivo.
                </p>
              </>
            ) : (
              <>
                <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Lock className="w-10 h-10 text-orange-500" />
                </div>
                <h2 className="text-xl font-bold mb-3">
                  Los talleres están disponibles en la versión completa
                </h2>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  Participa en talleres en vivo con un profesor nativo y accede a las grabaciones
                </p>
                <Link
                  to="/planes"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition-colors"
                >
                  Desbloquear ahora
                  <ChevronRight className="w-5 h-5" />
                </Link>
              </>
            )}
          </div>
        </div>
      )}

      {/* Workshop cards */}
      {hasFullAccess && workshops.length > 0 ? (
        <div className="grid gap-4">
          {workshops.map((w) => {
            const unitStatus = getUnitStatus(w.unit_id)
            const isLocked = unitStatus === 'locked'
            const attendance = attendanceMap[w.id]
            const attended = attendance?.status === 'attended'
            const wsStatus = w.status || 'scheduled'
            const hasRecording = !!w.recording_url
            const isLive = wsStatus === 'live'
            const isCompleted = wsStatus === 'completed'
            const isScheduled = wsStatus === 'scheduled'
            const now = new Date()
            const scheduledDate = w.scheduled_date ? new Date(w.scheduled_date) : null
            const isUpcoming = isScheduled && scheduledDate && scheduledDate > now
            const oneHourAfterStart = scheduledDate ? new Date(scheduledDate.getTime() + 60 * 60 * 1000) : null
            const isPastEntry = oneHourAfterStart ? now > oneHourAfterStart : false
            const unitInfo = w.unit || units.find(u => u.id === w.unit_id)
            const description = getDescription(w)

            return (
              <div
                key={w.id}
                className={`bg-card rounded-xl border overflow-hidden transition-opacity ${
                  isLocked ? 'opacity-50' : ''
                }`}
              >
                {/* Status banner */}
                {isLive && (
                  <div className="bg-red-600 text-white px-4 py-2 flex items-center gap-2 text-sm font-medium">
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                    ¡En vivo ahora!
                  </div>
                )}

                <div className="p-5">
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0 ${
                      isLocked
                        ? 'bg-gray-100'
                        : isLive
                          ? 'bg-red-100'
                          : hasRecording
                            ? 'bg-green-100'
                            : isUpcoming
                              ? 'bg-purple-100'
                              : 'bg-blue-100'
                    }`}>
                      {isLocked ? (
                        <Lock className="w-7 h-7 text-gray-400" />
                      ) : isLive ? (
                        <Radio className="w-7 h-7 text-red-600" />
                      ) : hasRecording ? (
                        <Play className="w-7 h-7 text-green-600" />
                      ) : (
                        <Video className="w-7 h-7 text-purple-600" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {unitInfo && (
                          <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded">
                            Unidad {unitInfo.order_num}
                          </span>
                        )}
                        {attended && (
                          <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded inline-flex items-center gap-1">
                            <Award className="w-3 h-3" />
                            Asistido
                          </span>
                        )}
                        {isLocked && (
                          <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                            Unidad bloqueada
                          </span>
                        )}
                      </div>

                      <h3 className="text-lg font-semibold mb-1">{getTitle(w)}</h3>

                      {unitInfo && (
                        <p className="text-sm text-muted-foreground mb-2">
                          {unitInfo.title}
                        </p>
                      )}

                      {description && !isLocked && (
                        <p className="text-sm text-muted-foreground mb-3">
                          {description}
                        </p>
                      )}

                      {/* Meta info */}
                      {!isLocked && (
                        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                          {scheduledDate && (
                            <span className="inline-flex items-center gap-1.5">
                              <Calendar className="w-4 h-4" />
                              {scheduledDate.toLocaleDateString(
                                'es-ES',
                                { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }
                              )}
                            </span>
                          )}
                          {scheduledDate && (
                            <span className="inline-flex items-center gap-1.5">
                              <Clock className="w-4 h-4" />
                              {scheduledDate.toLocaleTimeString(
                                'es-ES',
                                { hour: '2-digit', minute: '2-digit' }
                              )}
                            </span>
                          )}
                          {w.duration_minutes && (
                            <span>{w.duration_minutes} min</span>
                          )}
                          {w.max_participants && (
                            <span className="inline-flex items-center gap-1.5">
                              <Users className="w-4 h-4" />
                              {w.max_participants} máx
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  {!isLocked && (
                    <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t">
                      {isLive && (
                        <button
                          onClick={() => openMeetingUrl(w.meeting_url)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700"
                        >
                          <Radio className="w-4 h-4" />
                          Unirse en vivo
                        </button>
                      )}
                      {hasRecording && (
                        <button
                          onClick={() => openMeetingUrl(w.recording_url)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
                        >
                          <Play className="w-4 h-4" />
                          Ver grabación
                        </button>
                      )}
                      {!isCompleted && !isLive && !isPastEntry && (
                        <button
                          onClick={() => handleWorkshopClick(w)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
                        >
                          <Video className="w-4 h-4" />
                          Ir al Taller
                        </button>
                      )}
                      {attended && (
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg text-sm font-medium">
                          <CheckCircle className="w-4 h-4" />
                          Asistido
                        </div>
                      )}
                      {isPastEntry && (
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-500 rounded-lg text-sm font-medium cursor-not-allowed">
                          <CheckCircle className="w-4 h-4" />
                          Taller ya realizado
                        </div>
                      )}
                      {isCompleted && !hasRecording && (
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm">
                          <CheckCircle className="w-4 h-4" />
                          Finalizado — grabación disponible pronto
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      ) : hasFullAccess ? (
        <div className="bg-card rounded-xl border p-12 text-center">
          <Video className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h2 className="text-lg font-semibold mb-2">
            No hay talleres disponibles
          </h2>
          <p className="text-muted-foreground">
            Los talleres serán programados pronto
          </p>
        </div>
      ) : null}

      {/* Modal: Taller no disponible */}
      {showNotAvailableModal && selectedWorkshop && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-full bg-yellow-100">
                  <AlertTriangle className="w-6 h-6 text-yellow-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Taller no disponible
                </h3>
              </div>
              <button
                onClick={() => setShowNotAvailableModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-6">
              <p className="text-gray-600 mb-4">
                El link de acceso solo estara disponible <strong>10 minutos antes</strong> del horario programado.
              </p>
              <div className="bg-purple-50 rounded-lg p-4">
                <p className="text-sm text-purple-800 font-medium">
                  Fecha y hora del taller:
                </p>
                <p className="text-purple-900 font-semibold mt-1">{formatWorkshopDateTime(selectedWorkshop)}</p>
              </div>
            </div>
            <button
              onClick={() => setShowNotAvailableModal(false)}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              Entendido
            </button>
          </div>
        </div>
      )}

      {/* Modal: Confirmar asistencia */}
      {showConfirmModal && selectedWorkshop && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-8 max-w-lg w-full mx-4 shadow-2xl">
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mb-4">
                <Video className="w-8 h-8 text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Confirmar Asistencia
              </h2>
              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6 mb-6">
                <AlertTriangle className="w-10 h-10 text-yellow-500 mx-auto mb-3" />
                <p className="text-lg font-semibold text-gray-900 mb-2">
                  IMPORTANTE
                </p>
                <p className="text-gray-700">
                  Si usted avanza al siguiente paso, se registrará su asistencia como <strong>"ASISTIDO"</strong> al taller.
                </p>
                <p className="text-gray-700 mt-3 font-medium">
                  ¿Seguro que desea avanzar?
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                disabled={isRegistering}
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmAttendance}
                disabled={isRegistering}
                className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:opacity-50"
              >
                {isRegistering ? 'Registrando...' : 'Si, Ir al Taller'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
